import React, { useState } from 'react';
import { NavigationProps } from '../types/navigation';
import { Button } from '../components/ui/Button';
import { MapPin, Lock, Info, BellRing } from 'lucide-react';
import { useStore, isDropUnlockedBySequence, hasActiveReservation as checkActiveReservation, canReserveNewDrop } from '../lib/store';
export function EventsPage({
  onNavigate
}: NavigationProps) {
  const {
    events,
    addReservation,
    reservations,
    user
  } = useStore();
  const [showPointsInfo, setShowPointsInfo] = useState(false);
  // A returning user must review their unlocked box before reserving the next
  // drop — surface the review prompt and gate reservations until it's done.
  const hasPendingReview = reservations.some(r => r.status === 'redeemed');
  // One reservation at a time: must redeem the current reserved drop, then
  // review that catch, before a new drop can be reserved.
  const hasReserved = checkActiveReservation(reservations);
  const canReserve = canReserveNewDrop(reservations);
  // Has the user caught a box before (any reservation at all)?
  const hasCaught = reservations.length > 0;
  // Drops the user can still reserve = those they haven't already caught or
  // reserved. Once they've reviewed and there are none left, we notify them.
  const reservableDrops = events.filter(e => !reservations.some(r => r.eventId === e.id));
  const showNotifyBanner = hasCaught && !hasPendingReview && reservableDrops.length === 0;
  return <div className="min-h-screen bg-brand-ice pb-32" data-id="element-1875" data-mp="50j7mk4gjtcl">
      <div className="px-6 pt-5 pb-5 sticky top-0 z-10 bg-brand-ice/95 backdrop-blur flex justify-between items-center" data-id="element-1876" data-mp="9u458b252lb9">
        <div data-id="element-1877" data-mp="3s8wnrpmqcu6c">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-brand-purple mb-1" data-id="element-1878" data-mp="3s4z492w8q0lx">
            Catch board
          </p>
          <h1 className="text-4xl font-display text-brand-navy tracking-tight" data-id="element-1879" data-mp="3i8dxod4kohfz">
            Fishing Spots
          </h1>
        </div>
        <button onClick={() => setShowPointsInfo(true)} className="text-brand-purple text-xs font-bold flex items-center bg-white px-4 py-2.5 rounded-full shadow-[0_8px_20px_rgba(22,38,95,0.08)]" data-id="element-1880" data-mp="3n7h2bwgn4lnf">
          <Info className="w-4 h-4 mr-1" data-id="element-1881" data-mp="2zakj216cl4i6" />
          Points Guide
        </button>
      </div>

      <div className="p-4 space-y-4" data-id="element-1882" data-mp="2ubg8b63ryypq">
        {hasPendingReview && <div className="bg-yellow-50 border-2 border-yellow-400 rounded-2xl p-5 shadow-md animate-in slide-in-from-top-4" data-id="element-1883" data-mp="2pfsgeixwrrgj">
            <div className="flex items-start space-x-4" data-id="element-1884" data-mp="3n05bdaoe5e44">
              <div className="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center shrink-0" data-id="element-1885" data-mp="3i46h56gdt1vs">
                <span className="text-2xl" data-id="element-1886" data-mp="3d2quxb2gwpbt">⭐</span>
              </div>
              <div className="flex-1" data-id="element-1887" data-mp="385gyvckkpff0">
                <h2 className="text-lg font-bold text-yellow-900 mb-1" data-id="element-1888" data-mp="384fkay4cmasj">
                  Review to reserve your next drop
                </h2>
                <p className="text-yellow-800 text-sm mb-3" data-id="element-1889" data-mp="1wq232c43otlh">
                  You have a catch waiting to be reviewed. Finish your review to
                  unlock and reserve the next drop.
                </p>
                <Button onClick={() => onNavigate('review-flow')} className="w-full bg-yellow-400 hover:bg-yellow-500 text-black border-none shadow-sm" data-id="element-1890" data-mp="h6b7spgrepes">
                  Review Now
                </Button>
              </div>
            </div>
          </div>}

        {/* Reviewed, but nothing new to reserve yet → notify the user. */}
        {showNotifyBanner && <div className="bg-brand-blue rounded-2xl p-5 shadow-md animate-in slide-in-from-top-4" data-id="element-1891" data-mp="zw13rfw9dh8a">
            <div className="flex items-start space-x-4" data-id="element-1892" data-mp="14xqaijcg78qk">
              <div className="w-12 h-12 bg-brand-yellow rounded-full flex items-center justify-center shrink-0" data-id="element-1893" data-mp="19v9u7yrx0dpw">
                <BellRing className="w-6 h-6 text-brand-navy" data-id="element-1894" data-mp="36olcjsx9045q" />
              </div>
              <div className="flex-1" data-id="element-1895" data-mp="3bmmsi4zdnncz">
                <h2 className="text-lg font-bold text-white mb-1" data-id="element-1896" data-mp="3bno9eq32r42m">
                  You're all caught up!
                </h2>
                <p className="text-brand-light/90 text-sm" data-id="element-1897" data-mp="160p4w6x1efry">
                  No new drops are out right now. We'll text you the moment the
                  next one is ready to reserve.
                </p>
              </div>
            </div>
          </div>}

        {/* Instructions */}
        <div className="bg-brand-lavender rounded-[1.75rem] p-6 border border-white shadow-[0_12px_30px_rgba(22,38,95,0.06)] mb-5" data-id="element-1898" data-mp="1avkw54ug8z40">
          <div className="flex items-center gap-2 mb-2" data-id="element-1899" data-mp="i9jqzxanpxka">
            <span className="w-8 h-8 rounded-full bg-brand-purple text-white text-sm flex items-center justify-center" data-id="element-1900" data-mp="iauqvm0zel64">🎣</span>
            <h2 className="text-xl font-display text-brand-navy" data-id="element-1901" data-mp="r9pxgz7xtx9y">
              Upcoming Drops
            </h2>
          </div>
          <p className="text-brand-navy/65 text-sm leading-relaxed" data-id="element-1902" data-mp="3jc517lfxweu">
            Reserve a catch, collect it in person, then level up your crew.
            Some drops unlock as your points grow.
          </p>
        </div>

        {events.length === 0 && <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-gray-100" data-id="element-1903" data-mp="3li4zoqiy0fl2">
            <div className="w-16 h-16 bg-brand-blue/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1904" data-mp="3qcj39g7rdt0t">
              <MapPin className="w-8 h-8 text-brand-blue" data-id="element-1905" data-mp="3gkn0hruq9eq9" />
            </div>
            <h3 className="text-lg font-bold text-brand-blue mb-2" data-id="element-1906" data-mp="3gik14h7oq98v">
              No spots available
            </h3>
            <p className="text-gray-500 text-sm" data-id="element-1907" data-mp="d3wfo6uhkx4i">
              We are scouting new locations. Check back soon!
            </p>
          </div>}

        {events.map(event => {
        const isReserved = reservations.some(r => r.eventId === event.id);
        const isEligible = isDropUnlockedBySequence(events, reservations, event.id);
        const pointsLocked = (user?.points || 0) < event.requiredPoints;
        // Lock a not-yet-reserved drop when points are too low, a review is
        // owed, an active reservation already exists (one at a time), or the
        // previous drop hasn't been completed.
        const isLocked = (pointsLocked || hasPendingReview || hasReserved || !isEligible) && !isReserved && event.type === 'reservation';
        const lockReason = pointsLocked ? 'points' : hasPendingReview ? 'review' : hasReserved ? 'redeem' : 'sequence';
        return <div key={event.id} className={`bg-white rounded-[1.75rem] p-5 shadow-[0_14px_34px_rgba(22,38,95,0.08)] border border-white flex flex-col space-y-4 relative overflow-hidden ${isLocked ? 'opacity-75' : ''}`} data-id="element-1908" data-mp="6qaaxt7vwe6n">
              {isLocked && <div className="absolute inset-0 bg-gray-100/80 backdrop-blur-[2px] z-10 flex flex-col items-center justify-center p-4 text-center" data-id="element-1909" data-mp="1w0hxko9p62m2">
                  <div className="bg-white p-4 rounded-2xl shadow-xl max-w-[200px] w-full" data-id="element-1910" data-mp="1ym8w2lkmlc0h">
                    <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-2" data-id="element-1911" data-mp="23llg6yk444zd">
                      <Lock className="w-5 h-5 text-yellow-600" data-id="element-1912" data-mp="28dln5aylny5g" />
                    </div>
                    <span className="text-sm font-bold text-gray-900 block mb-1" data-id="element-1913" data-mp="2dav9ghqoztp2">
                      {lockReason === 'points' ? 'Level Too Low' : lockReason === 'review' ? 'Review Required' : lockReason === 'redeem' ? 'Redeem First' : 'Drop Locked'}
                    </span>
                    <span className="text-xs text-gray-500 block mb-3" data-id="element-1914" data-mp="1ujm14bsnraz2">
                      {lockReason === 'points' ? `Need ${event.requiredPoints} points` : lockReason === 'review' ? 'Review your last catch to unlock' : lockReason === 'redeem' ? 'Redeem your reserved drop first — one at a time' : 'Complete the previous drop first'}
                    </span>
                    {lockReason === 'review' && <Button size="sm" onClick={() => onNavigate('review-flow')} className="w-full bg-yellow-400 hover:bg-yellow-500 text-black border-none" data-id="element-1915" data-mp="3w2ekn54n0hm9">
                        Review Now
                      </Button>}
                    {lockReason === 'redeem' && <Button size="sm" onClick={() => onNavigate('catches')} className="w-full bg-yellow-400 hover:bg-yellow-500 text-black border-none" data-id="element-1916" data-mp="iqe5lik8owxa">
                        My Catches
                      </Button>}
                  </div>
                </div>}

              <div className="flex justify-between items-start" data-id="element-1917" data-mp="14w6w4672gnxr">
                <div className="flex items-start space-x-3" data-id="element-1918" data-mp="q95ajrvxjb3c">
                  {event.brandLogo && <div className="w-14 h-14 rounded-2xl bg-brand-ice border border-brand-lavender flex items-center justify-center p-2 shrink-0" data-id="element-1919" data-mp="v76qw51z0wb4">
                      <img src={event.brandLogo} alt={event.brand} className="max-w-full max-h-full object-contain mix-blend-multiply" data-id="element-1920" data-mp="h6maffjj3cks" />
                    </div>}
                  <div data-id="element-1921" data-mp="c9m371ay03s3">
                    <span className="text-[10px] font-bold text-brand-purple uppercase tracking-[0.16em]" data-id="element-1922" data-mp="c92u8a39spq7">
                      {event.theme}
                    </span>
                    <h3 className="font-display text-brand-navy text-xl leading-tight" data-id="element-1923" data-mp="26lkdl6kzn5pk">
                      {event.title}
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-1924" data-mp="1i25opmj28s6h">
                      {event.brand}
                      {event.date ? ` • ${event.date}` : ''}
                    </p>
                  </div>
                </div>
                <div className="bg-brand-lavender px-3 py-1.5 rounded-full text-[11px] font-bold text-brand-purple shrink-0 ml-2" data-id="element-1925" data-mp="18bt8g6ia3l65">
                  {event.time || (event.date ? '' : 'Available now')}
                </div>
              </div>

              {(event.instagramUrl || event.websiteUrl) && <div className="flex space-x-2 mt-1" data-id="element-1926" data-mp="1m4ita7m2mk1o">
                  {event.instagramUrl && <a href={event.instagramUrl} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-pink-600 bg-pink-50 px-2 py-1 rounded-md hover:bg-pink-100 transition-colors" data-id="element-1927" data-mp="1r2164937ljey">
                      Instagram
                    </a>}
                  {event.websiteUrl && <a href={event.websiteUrl} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded-md hover:bg-blue-100 transition-colors" data-id="element-1928" data-mp="2ovtoxklfaccm">
                      Website
                    </a>}
                </div>}

              <div className="flex items-center text-sm text-brand-navy/65 bg-brand-ice p-3 rounded-2xl" data-id="element-1929" data-mp="263r7w84vcu1u">
                <MapPin className="w-4 h-4 mr-2 text-gray-400" data-id="element-1930" data-mp="2jz3d59gni2x0" />
                <span className="truncate" data-id="element-1931" data-mp="2f4z3gig03gs8">{event.address}</span>
              </div>

              <div className="flex items-center justify-between pt-2" data-id="element-1932" data-mp="1rfweepj1elnp">
                <span className="text-sm font-bold text-brand-navy/60" data-id="element-1933" data-mp="1nfm9zhaw83ay">
                  <span className="text-brand-purple" data-id="element-1934" data-mp="1ndjiesu3e244">{event.boxesLeft}</span> / {event.totalBoxes} catches left
                </span>
                <Button size="sm" onClick={() => {
              if (isReserved) return;
              if (hasPendingReview) {
                onNavigate('review-flow');
                return;
              }
              if (hasReserved) {
                onNavigate('catches');
                return;
              }
              if (!canReserve || !isEligible || pointsLocked) return;
              addReservation(event.id);
            }} disabled={isReserved || isLocked && !hasPendingReview && !hasReserved} className={isReserved ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'px-6'} data-id="element-1935" data-mp="3rip921n0bqod">
                  {isReserved ? 'Reserved' : hasPendingReview ? 'Review to reserve' : hasReserved ? 'Redeem first' : 'Reserve'}
                </Button>
              </div>
            </div>;
      })}
      </div>

      {/* Points Info Modal */}
      {showPointsInfo && <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-in fade-in" data-id="element-1936" data-mp="1yneeq6yo5sxy">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm relative" data-id="element-1937" data-mp="1uj6p87ke08yo">
            <button onClick={() => setShowPointsInfo(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600" data-id="element-1938" data-mp="1pmq1vfl0hymn">
              ✕
            </button>

            <h2 className="text-xl font-bold mb-4 text-brand-blue" data-id="element-1939" data-mp="3jyd6tuxmbfgr">
              How to earn points
            </h2>
            <div className="space-y-4" data-id="element-1940" data-mp="3f1edvfpoxek5">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-100" data-id="element-1941" data-mp="2cngixao6ght5">
                <span className="font-medium text-gray-700" data-id="element-1942" data-mp="2gpacz03vankc">
                  Invite a friend
                </span>
                <span className="font-bold text-brand-yellow bg-brand-blue px-2 py-1 rounded-lg" data-id="element-1943" data-mp="22ny396favi6w">
                  +40 pts
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-100" data-id="element-1944" data-mp="1szg2uqaxnd3s">
                <span className="font-medium text-gray-700" data-id="element-1945" data-mp="1xq68m4n9thz0">
                  Leave a review
                </span>
                <span className="font-bold text-brand-yellow bg-brand-blue px-2 py-1 rounded-lg" data-id="element-1946" data-mp="1jzt1u5m489b9">
                  +30 pts
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-100" data-id="element-1947" data-mp="1a0lr7q6rmsru">
                <span className="font-medium text-gray-700" data-id="element-1948" data-mp="1ezokyehapcki">
                  Redeem a catch
                </span>
                <span className="font-bold text-brand-yellow bg-brand-blue px-2 py-1 rounded-lg" data-id="element-1949" data-mp="veejo8s587nk">
                  +20 pts
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-100" data-id="element-1950" data-mp="15bj6mattc75f">
                <span className="font-medium text-gray-700" data-id="element-1951" data-mp="10cgcvmja9ncr">
                  Reserve a spot
                </span>
                <span className="font-bold text-brand-yellow bg-brand-blue px-2 py-1 rounded-lg" data-id="element-1952" data-mp="1eb6ar4dxfr26">
                  +10 pts
                </span>
              </div>
            </div>
            <Button onClick={() => setShowPointsInfo(false)} className="w-full mt-6" data-id="element-1953" data-mp="1o2k938u56ktq">
              Got it
            </Button>
          </div>
        </div>}
    </div>;
}